import { fetchGenerated, getGenerateEndpoint, refreshGenerated } from '../core/fetch-generated';
import type { GeneratedOptions } from '../core/types';
import type { PromptOfTheDayOptions, PromptOfTheDayPrompt, PromptOfTheDayRequest } from '../types';
import { parsePromptsFromOutput } from './prompt-data';

export const PROMPT_OF_THE_DAY_SLUG = 'prompt-of-the-day';

export const PROMPT_OF_THE_DAY_ENDPOINT: string = getGenerateEndpoint(PROMPT_OF_THE_DAY_SLUG);

/**
 * A suggestion is stale once the day it belongs to is over. A rolling 24h window would instead keep
 * yesterday's prompt until the same hour tomorrow for anyone who loaded the page late in the day.
 */
const getNextLocalMidnight = (now: number): number => {
	const midnight = new Date(now);
	midnight.setHours(24, 0, 0, 0);
	return midnight.getTime();
};

/** Module-level so the identities are stable, which is what lets the hook depend on them. */
export const PROMPT_OF_THE_DAY_OPTIONS: Omit<
	GeneratedOptions<readonly PromptOfTheDayPrompt[]>,
	'fetchFn'
> = {
	parse: parsePromptsFromOutput,
	getExpiresAt: getNextLocalMidnight,
};

/**
 * Resolves today's suggestions for an account, or `null` when there are none to show - the request
 * failed, the serve route skipped this account, or the response was malformed. Never rejects.
 */
export const fetchPromptOfTheDay = (
	{ accountId, args }: PromptOfTheDayRequest,
	{ fetchFn }: PromptOfTheDayOptions = {},
): Promise<readonly PromptOfTheDayPrompt[] | null> =>
	fetchGenerated(
		{ slug: PROMPT_OF_THE_DAY_SLUG, accountId, args },
		{ ...PROMPT_OF_THE_DAY_OPTIONS, fetchFn },
	);

// Like `fetchPromptOfTheDay`, but always asks for a new generation, skipping any cached answer.
// Joins an already-open request instead of starting a second one. Never rejects.
export const refreshPromptOfTheDay: (
	request: PromptOfTheDayRequest,
	options?: PromptOfTheDayOptions,
) => Promise<readonly PromptOfTheDayPrompt[] | null> = ({ accountId, args }, { fetchFn } = {}) =>
	refreshGenerated(
		{ slug: PROMPT_OF_THE_DAY_SLUG, accountId, args },
		{ ...PROMPT_OF_THE_DAY_OPTIONS, fetchFn },
	);
